/* eslint-disable arrow-body-style */

import {action, autorun, computed, makeObservable, observable} from "mobx";
import {AppConfiguration} from "../core/config/app-configuration";
import {AuftragsverwaltungApiConfiguration} from "../config/auftragsverwaltung-api-configuration";

import {Project} from "../models/project";
import {MapState} from "../core/map-state";

import {PartialOrder, PartialOrderType} from "../models/partial-order";
import {Contractor} from "../models/contractor";
import {Journal} from "../models/journal";

import {ProcessSource} from "../models/process-source";
import {ProjectProcess} from "../models/project-process";
import {ProcessLog} from "../models/process-log";
import {MapViewerConfiguration} from "../config/map-viewer-configuration";
import {DropdownItemProps} from "semantic-ui-react/dist/commonjs/modules/Dropdown/DropdownItem";

import {Municipality} from "../models/municipality";
import {ProjectMunicipality} from "../models/project-municipality";
import {StoreType} from "./store-type";
import {Parcel} from "../models/parcel";
import {ProjectPerimeter} from "../models/project-perimeter";
import {Document} from "../models/document";

import {LayerObjectType} from "./layer-object-type";
import {PartialOderStoreType, ProjectStore} from "./project-store-mobx";
import {ProjectProcessStore} from "./project-process-store-mobx";
import {ProcessLogStore} from "./process-log-store-mobx";
import {ProjectPerimeterStore} from "./project-perimeter-store-mobx";
import {PartialOrderStore} from "./partial-order-store-mobx";
import {ProcessSourceStore} from "./process-source-store-mobx";
import {ContractorStore} from "./contractor-store-mobx";
import {JournalStore} from "./journal-store-mobx";
import {MunicipalityStore} from "./municipality-store-mobx";
import {ProjectMunicipalityStore} from "./project-municipality-store-mobx";
import {ParcelStore} from "./parcel-store-mobx";
import {IStore} from "./abstract-store";
import {DocumentStore} from "./document-store-mobx";

export type ProjectType = "gefahrenkarte" | "risikoübersicht";
export type EditingObjectType = "project" | "contractor" | "partial-order-gk" | "partial-order-ghk" |
"partial-order-risk" | "perimeter" | "document";
export type PerimeterLayerType = "perimeter-ghk" | "perimeter-gk" | "perimeter-risk";

export enum PerimeterType {
    Gefahrenkarte = 1,
    Gefahrenhinweiskarte = 2,
    Risikouebersicht = 3
}

export interface AuftragsverwaltungProps {
    config: AppConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    mapStore: MapState;
}

export type AuftragsverwaltungStatus = "loading" | "successful" | "error" | "not-found" | null;

export class Auftragsverwaltung {
    @observable loading: boolean;
    @observable status: AuftragsverwaltungStatus;

    @observable partialOrdersGhk = observable.map<number, PartialOrder>();
    @observable partialOrdersGk = observable.map<number, PartialOrder>();
    @observable processSources = observable.map<number, ProcessSource>();

    /**
     * Domains stored by domain name
     */
    @observable domains = observable.map<string, __esri.CodedValueDomain>();
    /**
     * Domains stored by field name
     */
    @observable domainsByField =  observable.map<string, __esri.CodedValueDomain>();

    @observable perimeterProposal: __esri.Graphic | null;

    @observable showMap = true;
    @observable isEditing = false;
    @observable editingObjectType: EditingObjectType | null = null;
    @observable apiLoaded = false;
    @observable apiLoading = false;

    config: AppConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    apiConfig: AuftragsverwaltungApiConfiguration;
    mapStore: MapState;

    stores: Map<string, StoreType> = new Map<string, StoreType>();

    constructor(props: AuftragsverwaltungProps) {
        makeObservable(this);
        this.config = props.config;
        this.mapViewerConfig = props.mapViewerConfig;
        this.apiConfig = props.mapViewerConfig.auftragsverwaltungApi;
        this.mapStore = props.mapStore;

        // init the stores
        this.initStores(this.mapViewerConfig, this.apiConfig);
    }

    get ProjectStore(): ProjectStore {
        return this.stores.get("project") as ProjectStore;
    }
    get ProjectProcessStore(): ProjectProcessStore {
        return this.stores.get("project-process") as ProjectProcessStore;
    }
    get ProjectPerimeterStore(): ProjectPerimeterStore {
        return this.stores.get("project-perimeter") as ProjectPerimeterStore;
    }
    get ProcessLogStore(): ProcessLogStore {
        return this.stores.get("process-log") as ProcessLogStore;
    }
    get JournalStore(): JournalStore {
        return this.stores.get("journal") as JournalStore;
    }
    get ContractorStore(): ContractorStore {
        return this.stores.get("contractor") as ContractorStore;
    }
    get PartialOrderGkStore(): PartialOrderStore {
        return this.stores.get("partial-order-gk") as PartialOrderStore;
    }
    get PartialOrderGhkStore(): PartialOrderStore {
        return this.stores.get("partial-order-ghk") as PartialOrderStore;
    }
    get PartialOrderRiskStore(): PartialOrderStore {
        return this.stores.get("partial-order-risk") as PartialOrderStore;
    }
    get DocumentStore(): DocumentStore {
        return this.stores.get("document") as DocumentStore;
    }
    get ProcessSourceStore(): ProcessSourceStore {
        return this.stores.get("process-source") as ProcessSourceStore;
    }
    get MunicipalityStore(): MunicipalityStore {
        return this.stores.get("municipality") as MunicipalityStore;
    }
    get ParcelStore(): ParcelStore {
        return this.stores.get("parcel") as ParcelStore;
    }
    get ProjectMunicipalityStore(): ProjectMunicipalityStore {
        return this.stores.get("project-municipality") as ProjectMunicipalityStore;
    }

    get selectedProject(): Project | null {
        return this.ProjectStore?.selectedItem as Project;
    }
    get selectedContractor(): Contractor | null {
        return this.ContractorStore?.selectedItem as Contractor;
    }
    get selectedPartialOrderGk(): PartialOrder | null {
        return this.PartialOrderGkStore?.selectedItem as PartialOrder;
    }
    get selectedPartialOrderGhk(): PartialOrder | null {
        return this.PartialOrderGhkStore?.selectedItem as PartialOrder;
    }
    get selectedPartialOrderRisk(): PartialOrder | null {
        return this.PartialOrderRiskStore?.selectedItem as PartialOrder;
    }
    get selectedDocument(): Document | null {
        return this.DocumentStore?.selectedItem as Document;
    }
    get selectedProcessSource(): ProcessSource | null {
        return this.ProcessSourceStore?.selectedItem as ProcessSource;
    }
    get selectedJournal(): Journal | null {
        return this.JournalStore?.selectedItem as Journal;
    }
    get selectedProcess(): ProjectProcess | null {
        return this.ProjectProcessStore?.selectedItem as ProjectProcess;
    }
    get selectedPerimeter(): ProjectPerimeter | null {
        return this.ProjectPerimeterStore?.selectedItem as ProjectPerimeter;
    }
    get selectedParcel(): Parcel | null {
        return this.ParcelStore?.selectedItem as Parcel;
    }

    /**
     * If there is an API layer config and projectFilter === true, the layer is filterable
     */
    get filterableLayers(): __esri.FeatureLayer[] {
        const filterableLayers: __esri.FeatureLayer[] = [];
        const apiConfig = this.mapViewerConfig.auftragsverwaltungApi;
        this.mapStore.map?.layers.forEach((layer: __esri.FeatureLayer) => {
            const layerConfig = apiConfig.getLayerConfigByPortalId(layer.portalItem?.id, layer.layerId);
            if (layerConfig && layerConfig.projectFilter) {
                filterableLayers.push(layer);
            }
        });
        return filterableLayers;
    }

    @action
    resetSelect(): void {
        this.ProjectStore?.resetSelectedItem();
        this.PartialOrderGkStore?.resetSelectedItem();
        this.PartialOrderGhkStore?.resetSelectedItem();
        this.PartialOrderRiskStore?.resetSelectedItem();
        this.DocumentStore?.resetSelectedItem();
        this.ProcessSourceStore?.resetSelectedItem();
        this.JournalStore?.resetSelectedItem();
        this.ProjectProcessStore?.resetSelectedItem();
        this.ProjectPerimeterStore?.resetSelectedItem();
        this.ContractorStore?.resetSelectedItem();
    }

    @action
    setStatus(status: AuftragsverwaltungStatus): void {
        this.status = status;
    }
    @action
    resetStatus(): void {
        this.status = null;
    }

    @action
    setApiLoading(flag: boolean, loadedFlag?: boolean): void {
        this.apiLoading = flag;
        if (typeof loadedFlag !== "undefined") {
            this.apiLoaded = loadedFlag;
        }
    }

    @action
    setShowMap(show: boolean): void {
        this.showMap = show;
    }

    @action
    setEditing(editing: boolean, objectType?: EditingObjectType): void {
        this.isEditing = editing;
        this.editingObjectType = objectType || null;
        if (editing) {
            this.resetStatus();
        }
    }

    @computed
    get isEditingPerimeter(): boolean {
        return this.isEditing && this.editingObjectType === "perimeter";
    }

    @computed
    get isEditingProject(): boolean {
        return this.isEditing && this.editingObjectType === "project";
    }
    @computed
    get isEditingContractor(): boolean {
        return this.isEditing && this.editingObjectType === "contractor";
    }
    @computed
    get isEditingPartialOrderGk(): boolean {
        return this.isEditing && this.editingObjectType === "partial-order-gk";
    }
    @computed
    get isEditingPartialOrderRisk(): boolean {
        return this.isEditing && this.editingObjectType === "partial-order-risk";
    }
    @computed
    get isEditingPartialOrderGhk(): boolean {
        return this.isEditing && this.editingObjectType === "partial-order-ghk";
    }
    @computed
    get isEditingDocument(): boolean {
        return this.isEditing && this.editingObjectType === "document";
    }

    /**
     * Get domain and mep to dropdown item options
     *
     * @param layer
     * @param fieldName
     * @private
     */
    getDomainAsOptions(layer: LayerObjectType, fieldName: string): Promise<DropdownItemProps[] | null> {
        return new Promise((resolve) => {
            this.loadDomain(layer, fieldName).then((domain: __esri.CodedValueDomain) => {
                const options: DropdownItemProps[] = [];

                domain?.codedValues.forEach((codedValue: __esri.CodedValue) => {
                    options.push({
                        key: codedValue.code,
                        text: codedValue.name,
                        value: codedValue.code
                    });
                });
                resolve(options);
            });
        });
    }
    getShortDomainCodes(layerName: LayerObjectType, fieldName: string): Promise<Map<number, string> | null> {
        return new Promise((resolve) => {
            this.loadDomain(layerName, fieldName).then((domain: __esri.CodedValueDomain) => {
                const codes = new Map<number, string>();
                domain?.codedValues.forEach((codedValue: __esri.CodedValue) => {
                    const key = typeof codedValue.code === "string" ? parseInt(codedValue.code, 10) : codedValue.code;
                    codes.set(key, codedValue.name);
                });
                resolve(codes);
            });
        });
    }

    getStringDomainCodes(layerName: LayerObjectType, fieldName: string): Promise<Map<string, string> | null> {
        return new Promise((resolve) => {
            this.loadDomain(layerName, fieldName).then((domain: __esri.CodedValueDomain) => {
                const codes = new Map<string, string>();
                domain?.codedValues.forEach((codedValue: __esri.CodedValue) => {
                    const key = typeof codedValue.code === "number" ? codedValue.code.toString() : codedValue.code;
                    codes.set(key, codedValue.name);
                });
                resolve(codes);
            });
        });
    }

    /**
     * Load coded value domain by field name
     *
     * @param type Type of date. Is used to select the layer.
     * @param fieldName The name of the field the domain is related to
     */
    loadDomain(type: LayerObjectType, fieldName: string): Promise<__esri.CodedValueDomain | null> {
        return new Promise((resolve) => {
            autorun((reaction: any) => {
                if (!this.apiLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun

                if (!fieldName) {
                    throw new Error("Field name undefined");
                }
                const dbField = this.getDatabaseFieldName(type, fieldName);
                if (!dbField) {
                    throw new Error(`Database field not found for ${type} ${fieldName}`);
                }

                const store = this.stores.get(type);
                if (!store) {
                    throw new Error(`Store not found for ${type} ${fieldName}`);
                }

                store.loadDomain(dbField).then((domain) => {
                    resolve(domain);
                });
            });
        });
    }

    @action
    loadApi() {
        if (!this.apiLoaded && !this.apiLoading) {
            this.setApiLoading(true);

            // load all stores. Loads the feature layers for each store
            const stores = [...this.stores.values()];
            const apiPromises = stores.map((store: IStore) => {
                return store.loadApi();
            });
            Promise.all(apiPromises).then(() =>{
                // all feature layers loaded
                this.setApiLoading(false, true);
            });
        }
    }

    /**
     * Get field alias name for an object property
     * @param type
     * @param propertyName
     */
    getFieldAlias(type: LayerObjectType, propertyName: string): string | undefined {
        const dbFieldName = this.getDatabaseFieldName(type, propertyName);
        if (!dbFieldName) {
            // eslint-disable-next-line no-console
            console.warn(`No database field found for ${type} ${propertyName}`);
            return propertyName;
        }

        if (!this.apiLoaded) {
            // API should be loaded when domains are requested the first time
            throw new Error("API not loaded.");
        }

        const field = this.stores.get(type)?.getField(dbFieldName);
        return field?.alias;
    }

    /**
     * Returns the name of this filed in database.
     * Uses field name mappings in model classes.
     *
     * @param type
     * @param fieldName
     * @private
     */
    private getDatabaseFieldName(type: LayerObjectType, fieldName: string): string | undefined {
        switch(type) {
        case "project": {
            return Project.databaseFieldNames.get(fieldName);
        }
        case "project-process": {
            return ProjectProcess.databaseFieldNames.get(fieldName);
        }
        case "contractor": {
            return Contractor.databaseFieldNames.get(fieldName);
        }
        case "partial-order-gk": {
            return PartialOrder.databaseFieldNames.get(fieldName);
        }
        case "partial-order-ghk": {
            return PartialOrder.databaseFieldNames.get(fieldName);
        }
        case "partial-order-risk": {
            return PartialOrder.databaseFieldNames.get(fieldName);
        }
        case "document": {
            return Document.databaseFieldNames.get(fieldName);
        }
        case "process-source": {
            return ProcessSource.databaseFieldNames.get(fieldName);
        }
        case "process-log": {
            return ProcessLog.databaseFieldNames.get(fieldName);
        }
        case "journal": {
            return Journal.databaseFieldNames.get(fieldName);
        }
        case "municipality": {
            return Municipality.databaseFieldNames.get(fieldName);
        }
        case "project-municipality": {
            return ProjectMunicipality.databaseFieldNames.get(fieldName);
        }
        case "project-perimeter": {
            return ProjectPerimeter.databaseFieldNames.get(fieldName);
        }
        default: {
            throw new Error(`Could not get database field name. Type unknown. ${type}`);
        }
        }
    }

    /**
     * Init all data stores. Do this before loadApi()!!!
     * @param mapViewerConfig
     * @param apiConfig
     * @private
     */
    private initStores(mapViewerConfig: MapViewerConfiguration, apiConfig: AuftragsverwaltungApiConfiguration): void {
        // init journal and process log stores separately. They are needed in other stores
        const journalStore = this.initJournalStore(apiConfig);
        const processLogStore = this.initProcessLogStore(apiConfig);
        const partialOrderStores = this.initPartialOrderStores(apiConfig, journalStore);
        const documentStore = this.initDocumentStore(apiConfig, journalStore);
        const projectMunicipalityStore = this.initProjectMunicipalityStore(apiConfig, journalStore);
        this.initProjectStore(apiConfig, journalStore, processLogStore, partialOrderStores, documentStore, projectMunicipalityStore);
        this.initProcessSourceStore(apiConfig, journalStore);

        // init all other stores
        const stores = ["contractor", "municipality", "parcel"];
        stores.forEach((type: string) => {
            const layerConfig = apiConfig.getLayerConfig(type as LayerObjectType);
            if (!layerConfig) {
                throw new Error(`No layer configuration found for ${type}`);
            }
            if (type === "contractor") {
                this.stores.set(
                    type,
                    new ContractorStore({
                        layerConfig,
                        journalStore
                    })
                );
            }
            if (type === "municipality") {
                this.stores.set(type,
                    new MunicipalityStore({
                        layerConfig
                    })
                );
            }
            if (type === "parcel") {
                this.stores.set(type,
                    new ParcelStore({
                        layerConfig,
                        mapStore: this.mapStore
                    })
                );
            }
        });
    }

    private initJournalStore(apiConfig: AuftragsverwaltungApiConfiguration): JournalStore {
        const layerConfig = apiConfig.getLayerConfig("journal");
        if (!layerConfig) {
            throw new Error("Journal layer configuration not found.");
        }
        const journalStore = new JournalStore({
            layerConfig
        });
        if (!journalStore) {
            throw new Error("Error initializing journal store.");
        }
        this.stores.set("journal", journalStore);
        return journalStore;
    }

    private initProcessLogStore(apiConfig: AuftragsverwaltungApiConfiguration): ProcessLogStore {
        const layerConfig = apiConfig.getLayerConfig("process-log");
        if (!layerConfig) {
            throw new Error("Process log layer configuration not found.");
        }
        const processLogStore = new ProcessLogStore({
            layerConfig
        });
        if (!processLogStore) {
            throw new Error("Error initializing process log store.");
        }
        this.stores.set("process-log", processLogStore);
        return processLogStore;
    }

    private initProjectMunicipalityStore(apiConfig: AuftragsverwaltungApiConfiguration,
                                         journalStore: JournalStore): ProjectMunicipalityStore {
        const layerConfig = apiConfig.getLayerConfig("project-municipality");
        if (!layerConfig) {
            throw new Error("Project municipality layer configuration not found.");
        }
        const pmStore = new ProjectMunicipalityStore({
            layerConfig,
            journalStore
        });
        if (!pmStore) {
            throw new Error("Error initializing Project municipality store.");
        }
        this.stores.set("project-municipality", pmStore);
        return pmStore;
    }

    private initProjectStore(apiConfig: AuftragsverwaltungApiConfiguration,
                             journalStore: JournalStore,
                             processLogStore: ProcessLogStore,
                             partialOrderStores: Map<PartialOderStoreType, PartialOrderStore>,
                             documentStore: DocumentStore,
                             projectMunicipalityStore: ProjectMunicipalityStore): void {

        const processStore = this.initProcessStore(apiConfig, journalStore, processLogStore);
        if (!processStore) {
            throw new Error("Error initializing process store.");
        }
        this.stores.set("project-process", processStore);

        const perimeterStore = this.initPerimeterStore(apiConfig, journalStore);
        if (!perimeterStore) {
            throw new Error("Error initializing project permieter store.");
        }
        this.stores.set("project-perimeter", perimeterStore);

        const apiLayerConfig = apiConfig.getLayerConfig("project" as LayerObjectType);
        if (!apiLayerConfig) {
            throw new Error("Project layer configuration not found");
        }
        const projectStore = new ProjectStore({
            layerConfig: apiLayerConfig,
            journalStore,
            processStore,
            perimeterStore,
            partialOrderStores,
            documentStore,
            projectMunicipalityStore
        });
        this.stores.set("project", projectStore);
        perimeterStore.projectStore = projectStore;
    }

    private initProcessStore(apiConfig: AuftragsverwaltungApiConfiguration,
                             journalStore: JournalStore,
                             processLogStore: ProcessLogStore): ProjectProcessStore {
        const apiLayerConfig = apiConfig.getLayerConfig("project-process");
        if (!apiLayerConfig) {
            throw new Error("Process layer configuration not found.");
        }
        return new ProjectProcessStore({
            mapViewerConfig: this.mapViewerConfig,
            layerConfig: apiLayerConfig,
            journalStore,
            processLogStore
        });
    }
    private initPerimeterStore(apiConfig: AuftragsverwaltungApiConfiguration,
                               journalStore: JournalStore): ProjectPerimeterStore {
        const apiLayerConfig = apiConfig.getLayerConfig("project-perimeter");
        if (!apiLayerConfig) {
            throw new Error("Project perimeter layer configuration not found.");
        }
        return new ProjectPerimeterStore({
            layerConfig: apiLayerConfig,
            journalStore,
            mapStore: this.mapStore
        });
    }

    private initPartialOrderStores(apiConfig: AuftragsverwaltungApiConfiguration,
                                   journalStore: JournalStore): Map<PartialOderStoreType, PartialOrderStore> {
        const types: PartialOrderType[] = ["partial-order-gk", "partial-order-ghk", "partial-order-risk"];
        const partialOrderStores  = new Map<PartialOderStoreType, PartialOrderStore>();
        types.forEach((type: string) => {
            const layerConfig = apiConfig.getLayerConfig(type as LayerObjectType);
            if (!layerConfig) {
                throw new Error(`Layer configuration not found for type ${type}`);
            }
            const partialOrderStore =
                new PartialOrderStore({
                    partialOrderType: type as PartialOrderType,
                    layerConfig,
                    journalStore
                });
            this.stores.set(type, partialOrderStore);
            partialOrderStores.set(type as PartialOderStoreType, partialOrderStore);
        });
        return partialOrderStores;
    }

    private initDocumentStore(apiConfig: AuftragsverwaltungApiConfiguration,
                              journalStore: JournalStore): DocumentStore {
        const layerConfig = apiConfig.getLayerConfig("document");
        if (!layerConfig) {
            throw new Error("Document layer configuration not found.");
        }
        const docStore = new DocumentStore({
            layerConfig,
            journalStore
        });
        if (!docStore) {
            throw new Error("Error initializing Document store.");
        }
        this.stores.set("document", docStore);
        return docStore;
    }
    private initProcessSourceStore(apiConfig: AuftragsverwaltungApiConfiguration,
                                   journalStore: JournalStore): void {

        const layerConfig = apiConfig.getLayerConfig("process-source");
        if (!layerConfig) {
            throw new Error(`Layer configuration not found for process source.`);
        }
        this.stores.set(
            "process-source",
            new ProcessSourceStore({
                layerConfig,
                journalStore
            })
        );
    }
}
